<template>
    <div ref="rootRef" :class="['gh-tooltip', positionClass, variantClass]">
        <div
            :class="['gh-tooltip-content', contentClass]"
            :style="contentStyle"
        >
            <slot>{{ content }}</slot>
        </div>
    </div>
</template>

<script setup lang="ts">
    import {
        computed,
        CSSProperties,
        onBeforeUnmount,
        onMounted,
        ref,
        watch,
    } from 'vue';
    import {
        TooltipPosition,
        TooltipVariant,
    } from '@/types/components/base-components.types';

    // ----- props -----
    const props = withDefaults(
        defineProps<{
            content?: string;
            contentClass?: string | string[] | Record<string, boolean> | null;
            contentStyle?: CSSProperties;
            position?: TooltipPosition;
            variant?: TooltipVariant;
            isActive?: boolean;
        }>(),
        {
            content: '',
            contentClass: '',
            contentStyle: () => ({}),
            position: TooltipPosition.Left,
            variant: TooltipVariant.Primary,
            isActive: false,
        }
    );

    // ----- state -----
    const rootRef = ref<HTMLElement | null>(null);
    const hostElement = ref<HTMLElement | null>(null);

    // ----- computed -----
    // 轉換 tooltip 方向 class
    const positionClass = computed(() => {
        return `gh-tooltip-${props.position}`;
    });

    // 轉換 tooltip 顏色 class
    const variantClass = computed(() => {
        return `gh-tooltip-${props.variant}`;
    });

    // ----- methods -----
    // 綁定父層 hover，讓 tooltip 可獨立於 tour 使用
    const bindHoverEvents = (): void => {
        if (!rootRef.value || !rootRef.value.parentElement) return;
        if (props.isActive) return;
        if (rootRef.value.classList.contains('gh-tooltip-active')) return;

        hostElement.value = rootRef.value.parentElement;
        hostElement.value.addEventListener('mouseenter', onHostMouseEnter);
        hostElement.value.addEventListener('mouseleave', onHostMouseLeave);
    };

    // 解除父層 hover 綁定
    const unbindHoverEvents = (): void => {
        if (!hostElement.value) return;

        hostElement.value.removeEventListener('mouseenter', onHostMouseEnter);
        hostElement.value.removeEventListener('mouseleave', onHostMouseLeave);
        hostElement.value = null;
    };

    // 顯示 tooltip
    const onHostMouseEnter = (): void => {
        if (!rootRef.value) return;
        rootRef.value.classList.add('gh-tooltip-active');
    };

    // 隱藏 tooltip
    const onHostMouseLeave = (): void => {
        if (!rootRef.value) return;
        if (props.isActive) return;

        if (rootRef.value.dataset.tourActive === 'true') return;
        rootRef.value.classList.remove('gh-tooltip-active');
    };

    // ----- watch -----
    watch(
        () => props.isActive,
        (active) => {
            if (!rootRef.value) return;

            if (active) {
                unbindHoverEvents();
                rootRef.value.classList.add('gh-tooltip-active');
                return;
            }

            rootRef.value.classList.remove('gh-tooltip-active');
            bindHoverEvents();
        }
    );

    // ----- lifecycle -----
    onMounted(() => {
        if (props.isActive) {
            rootRef.value?.classList.add('gh-tooltip-active');
            return;
        }

        bindHoverEvents();
    });

    onBeforeUnmount(() => {
        unbindHoverEvents();
    });
</script>

<style lang="scss" scoped>
    @use 'sass:map';

    $tooltip-default-bg: #06294b;
    $tooltip-default-border: #d1d1d1;

    $tooltip-styles: (
        primary: (
            bg-color: $tooltip-default-bg,
            border-color: $tooltip-default-border,
        ),
        warning: (
            bg-color: #fc8f00,
            border-color: #ffc477,
        ),
    );

    $tooltip-config: (
        left: (
            tooltip-x: -105%,
            tooltip-y: 0,
            triangle-x-before: 103%,
            triangle-x-after: 102%,
            triangle-y-before: 50%,
            triangle-y-after: 50%,
            offset-y: -50%,
        ),
        right: (
            tooltip-x: 105%,
            tooltip-y: 0,
            triangle-x-before: -3%,
            triangle-x-after: -2%,
            triangle-y-before: 50%,
            triangle-y-after: 50%,
            offset-y: -50%,
        ),
        top: (
            tooltip-x: 0,
            tooltip-y: calc(-100% - 10px),
            triangle-x-before: 50%,
            triangle-x-after: 50%,
            triangle-y-before: 100%,
            triangle-y-after: calc(100% - 1px),
            offset-y: 0,
        ),
        bottom: (
            tooltip-x: 0,
            tooltip-y: calc(100% + 10px),
            triangle-x-before: 50%,
            triangle-x-after: 50%,
            triangle-y-before: -10px,
            triangle-y-after: -9px,
            offset-y: 0,
        ),
    );

    .gh-tooltip {
        position: absolute;
        max-width: 220px;
        width: max-content;
        opacity: 0;
        visibility: hidden;
        pointer-events: none;
        z-index: 2;
        transition: opacity 0.3s ease;
        will-change: opacity;

        .gh-tooltip-content {
            background-color: $tooltip-default-bg;
            color: #ffffff;
            padding: 8px 10px;
            border: 1px solid $tooltip-default-border;
            border-radius: 5px;
            font-size: 14px;
            word-wrap: break-word;
            white-space: normal;
        }

        &.gh-tooltip-active {
            opacity: 1;
            visibility: visible;
            pointer-events: auto;
        }

        // 設定 tooltip 顏色
        @each $style, $style-values in $tooltip-styles {
            $bg-color: map.get($style-values, bg-color);
            $border-color: map.get($style-values, border-color);

            &.gh-tooltip-#{$style} {
                .gh-tooltip-content {
                    background-color: $bg-color;
                    border: 1px solid $border-color;
                }
            }
        }

        // 設定 tooltip 位置
        @each $position, $values in $tooltip-config {
            $tooltip-x: map.get($values, tooltip-x);
            $tooltip-y: map.get($values, tooltip-y);
            $triangle-x-before: map.get($values, triangle-x-before);
            $triangle-x-after: map.get($values, triangle-x-after);
            $triangle-y-before: map.get($values, triangle-y-before);
            $triangle-y-after: map.get($values, triangle-y-after);
            $offset-y: map.get($values, offset-y);

            &.gh-tooltip-#{$position} {
                #{$position}: 0;

                @if $position == left or $position == right {
                    top: 50%;
                    transform: translate($tooltip-x, -50%);
                } @else {
                    left: 50%;
                    transform: translate(-50%, $tooltip-y);
                }

                &::before,
                &::after {
                    content: '';
                    position: absolute;
                    border-style: solid;
                    border-color: transparent;
                    transform: translate(-50%, $offset-y);
                }

                &::before {
                    left: $triangle-x-before;
                    top: $triangle-y-before;
                    border-width: 8px;
                    border-#{$position}-color: $tooltip-default-border;
                    z-index: -1;
                }

                &::after {
                    left: $triangle-x-after;
                    top: $triangle-y-after;
                    border-width: 6px;
                    border-#{$position}-color: $tooltip-default-bg;
                }

                // 設定三角形樣式
                @each $style, $style-values in $tooltip-styles {
                    $bg-color: map.get($style-values, bg-color);
                    $border-color: map.get($style-values, border-color);

                    &.gh-tooltip-#{$style}::before {
                        border-#{$position}-color: $border-color;
                    }

                    &.gh-tooltip-#{$style}::after {
                        border-#{$position}-color: $bg-color;
                    }
                }
            }
        }
    }
</style>
